import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, PlusCircle, BookOpen, TrendingUp, Calendar, Mail, Check, X, Flame, Settings, Sparkles, FileText, Home, Users, MessageCircle, Map, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import PersonalDreamMap from "@/components/PersonalDreamMap";
import GalaxyBackground from "@/components/GalaxyBackground";
import ScriptInitial from "@/components/ScriptInitial";
import MorningDreamPrompt from "@/components/MorningDreamPrompt";
import StreakConfetti from "@/components/StreakConfetti";
import ArtStylesShowcase from "@/components/ArtStylesShowcase";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [streak, setStreak] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState("week");
  const [summaryLanguage, setSummaryLanguage] = useState("finnish");

  // Streak rank system
  const getStreakRank = (currentStreak) => {
    if (currentStreak >= 100) return { key: 'oneironautti', color: 'from-amber-400 to-yellow-300', textColor: 'text-amber-600', level: 5 };
    if (currentStreak >= 75) return { key: 'valaistunut', color: 'from-rose-400 to-pink-400', textColor: 'text-rose-600', level: 4 };
    if (currentStreak >= 50) return { key: 'unisakavelija', color: 'from-purple-400 to-indigo-400', textColor: 'text-purple-600', level: 3 };
    if (currentStreak >= 21) return { key: 'unitulkki', color: 'from-teal-400 to-emerald-400', textColor: 'text-teal-600', level: 2 };
    if (currentStreak >= 3) return { key: 'unioivaltaja', color: 'from-violet-400 to-purple-400', textColor: 'text-violet-600', level: 1 };
    return { key: 'uneksija', color: 'from-slate-400 to-gray-400', textColor: 'text-void/70', level: 0 };
  };

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t('dashboard.goodMorning');
    if (hour >= 12 && hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  const allRanks = [
    { key: 'uneksija', min: 0, color: 'from-slate-400 to-gray-400', bg: 'bg-slate-400' },
    { key: 'unioivaltaja', min: 3, color: 'from-violet-400 to-purple-400', bg: 'bg-violet-400' },
    { key: 'unitulkki', min: 21, color: 'from-teal-400 to-emerald-400', bg: 'bg-teal-400' },
    { key: 'unisakavelija', min: 50, color: 'from-purple-400 to-indigo-400', bg: 'bg-purple-500' },
    { key: 'valaistunut', min: 75, color: 'from-rose-400 to-pink-400', bg: 'bg-rose-400' },
    { key: 'oneironautti', min: 100, color: 'from-amber-400 to-yellow-300', bg: 'bg-amber-400' },
  ];

  useEffect(() => {
    fetchStats();
    fetchInvites();
    fetchStreak();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dreams/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/circles/invites/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(response.data);
    } catch (error) {
      console.error("Failed to fetch invites:", error);
    }
  };

  const fetchStreak = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreak(response.data);
    } catch (error) {
      console.error("Failed to fetch streak:", error);
    }
  };

  const handleInviteResponse = async (inviteId, accept) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/circles/invites/${inviteId}/respond?accept=${accept}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setInvites(invites.filter(inv => inv.id !== inviteId));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dreams/summary`,
        { period: summaryPeriod, language: summaryLanguage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
      toast.success(t('dashboard.summary.generated') || 'Summary generated!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('common.error');
      console.error('Summary error:', errorMsg);
      toast.error(errorMsg, { duration: 10000 });
      alert('Virhe: ' + errorMsg);
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <GalaxyBackground />
      <MorningDreamPrompt user={user} />
      <StreakConfetti streak={streak} userId={user?.id} />
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/games')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-games"
                >
                  {t('games.navTitle') || 'Pelit'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/relaxation')}
                  className="font-body text-purple-500 hover:text-purple-600"
                  data-testid="nav-relaxation"
                >
                  {t('relaxation.title') || 'Rentoutuminen'}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:block">
                <LanguageSelector variant="minimal" />
              </div>
              {/* Mobile: Blog link, Desktop: hidden (already in nav) */}
              <Button
                variant="ghost"
                onClick={() => navigate('/blog')}
                className="sm:hidden font-body text-void hover:text-void/80 px-2 text-sm"
                data-testid="nav-blog-mobile"
              >
                {t("blog.title")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/games')}
                className="sm:hidden font-body text-void hover:text-void/80 px-2 text-sm"
                data-testid="nav-games-mobile"
              >
                {t('games.navTitle') || 'Pelit'}
              </Button>
              {/* Desktop: New Dream button, Mobile: hidden */}
              <Button
                onClick={() => navigate('/journal')}
                className="hidden sm:flex bg-void text-white hover:bg-void/90 rounded-full font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95 px-4 py-2 text-base"
                data-testid="new-dream-button"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {t('nav.newDream')}
              </Button>
              <button
                onClick={() => navigate('/settings')}
                className="relative group"
                data-testid="settings-button"
              >
                <span className="absolute -top-1 -right-1 text-amber-400 text-xs animate-pulse">✦</span>
                <span className="absolute -bottom-1 -left-1 text-amber-300 text-[10px] animate-pulse" style={{animationDelay: '0.5s'}}>✦</span>
                <div className="w-9 h-9 rounded-full border-2 border-emerald-400 flex items-center justify-center group-hover:border-emerald-500 transition-colors bg-white/50">
                  <Settings className="w-4 h-4 text-emerald-600" />
                </div>
              </button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Pending Invites */}
        {invites.length > 0 && (
          <div className="mb-8 glass-effect rounded-3xl p-6 border-2 border-lucid/30" data-testid="pending-invites">
            <h3 className="text-lg font-heading text-void mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-lucid" />
              {t('circles.pendingInvites')} ({invites.length})
            </h3>
            <div className="space-y-3">
              {invites.map(invite => (
                <div key={invite.id} className="flex items-center justify-between bg-white/50 rounded-xl p-4">
                  <div>
                    <p className="font-body text-void font-medium">{invite.circle_name}</p>
                    <p className="text-sm text-void/60 font-body">
                      {t('circles.invitedBy') || 'Invited by'}: {invite.invited_by_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleInviteResponse(invite.id, true)}
                      className="bg-lucid text-void hover:bg-lucid/90 rounded-full"
                      size="sm"
                      data-testid={`accept-invite-${invite.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {t('circles.accept')}
                    </Button>
                    <Button
                      onClick={() => handleInviteResponse(invite.id, false)}
                      variant="outline"
                      className="rounded-full border-void/20"
                      size="sm"
                      data-testid={`decline-invite-${invite.id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      {t('circles.decline')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-2" data-testid="welcome-message">
                <ScriptInitial>{getGreeting()}</ScriptInitial>, <span className="italic font-script">{user?.name}</span>
              </h1>
              <p className="text-lg text-void/70 font-body tracking-wide flex items-center gap-2" data-testid="streak-rank-display">
                {streak ? (
                  <>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r ${getStreakRank(streak.current_streak).color} text-white text-sm font-medium`}>
                      {t(`dashboard.streakRanks.${getStreakRank(streak.current_streak).key}`)}
                    </span>
                    <span className="text-void/50">|</span>
                    <span>{t('dashboard.sanctuary')}</span>
                  </>
                ) : (
                  <span>{t('dashboard.sanctuary')}</span>
                )}
              </p>

              {/* Horizontal Streak Rank Bar */}
              {streak && (
                <div className="mt-5 max-w-lg" data-testid="streak-rank-bar">
                  {/* Color bar */}
                  <div className="flex h-3 rounded-full overflow-hidden shadow-inner">
                    {allRanks.map((rank, i) => {
                      const isActive = getStreakRank(streak.current_streak).level >= i;
                      return (
                        <div
                          key={rank.key}
                          className={`flex-1 bg-gradient-to-r ${rank.color} transition-all duration-500 ${isActive ? 'opacity-100' : 'opacity-20'}`}
                        />
                      );
                    })}
                  </div>
                  {/* Labels under bar */}
                  <div className="flex mt-1.5">
                    {allRanks.map((rank, i) => {
                      const isActive = getStreakRank(streak.current_streak).level >= i;
                      const isCurrent = getStreakRank(streak.current_streak).level === i;
                      return (
                        <div key={rank.key} className="flex-1 text-center">
                          <span className={`text-[10px] leading-tight font-body block ${isCurrent ? 'font-bold text-void' : isActive ? 'font-medium text-void/70' : 'text-void/30'}`}>
                            {t(`dashboard.streakRanks.${rank.key}`)}
                          </span>
                          <span className={`text-[9px] ${isActive ? 'text-void/50' : 'text-void/20'}`}>
                            {rank.min}+
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {!user?.is_premium && (
              <Button
                onClick={() => navigate('/premium')}
                className="bg-gradient-to-r from-ethereal to-lucid text-void hover:opacity-90 rounded-full px-6 py-6 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid="upgrade-premium-button"
              >
                ✨ {t('dashboard.upgradePremium')}
              </Button>
            )}
            {user?.is_premium && (
              <button 
                onClick={() => navigate('/premium')}
                className="px-6 py-3 bg-lucid/20 rounded-full border border-lucid/30 hover:bg-lucid/30 transition-colors flex items-center gap-2 cursor-pointer" 
                data-testid="premium-badge"
              >
                <span className="text-void font-body font-medium">✨ Premium</span>
                <Settings className="w-4 h-4 text-void/60" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-stats">
            <p className="text-void/60 font-body">{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {/* Dream Streak Banner */}
            {streak && (
              <div className={`glass-effect rounded-3xl p-6 mb-8 ${streak.streak_active ? 'border-2 border-orange-400/50' : ''}`} data-testid="streak-banner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${streak.streak_active && streak.current_streak > 0 ? 'bg-gradient-to-br from-orange-400 to-red-500' : 'bg-void/10'}`}>
                      <Flame className={`w-7 h-7 ${streak.streak_active && streak.current_streak > 0 ? 'text-white' : 'text-void/40'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-heading font-light text-void">{streak.current_streak}</span>
                        <span className="text-lg text-void/70 font-body">{t('dashboard.dayStreak') || 'day streak'}</span>
                        {streak.streak_active && streak.current_streak > 0 && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${getStreakRank(streak.current_streak).color} text-white`}>
                            {t(`dashboard.streakRanks.${getStreakRank(streak.current_streak).key}`)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-void/60 font-body">
                        {streak.longest_streak > 0 && (
                          <span>{t('dashboard.longestStreak') || 'Longest'}: {streak.longest_streak} {t('dashboard.days') || 'days'}</span>
                        )}
                      </p>
                      {streak.streak_active && streak.current_streak > 0 && (() => {
                        // Next 7-day milestone
                        const nextMs = (Math.floor(streak.current_streak / 7) + 1) * 7;
                        const daysLeft = nextMs - streak.current_streak;
                        if (daysLeft > 7) return null;
                        return (
                          <p className="text-xs text-void/50 font-body mt-1 italic" data-testid="streak-next-reward">
                            ✨ {t('dashboard.streakNextMilestone', { days: daysLeft, milestone: nextMs })}
                          </p>
                        );
                      })()}
                      {streak.streak_active && streak.current_streak > 0 && (() => {
                        // Progress bar — every 7 days is a milestone
                        const next = (Math.floor(streak.current_streak / 7) + 1) * 7;
                        const prev = Math.floor(streak.current_streak / 7) * 7;
                        const pct = Math.min(100, Math.round(((streak.current_streak - prev) / (next - prev)) * 100));
                        return (
                          <div className="mt-2 w-full max-w-xs" data-testid="streak-progress">
                            <div className="flex items-center justify-between text-[11px] text-void/50 font-body mb-1">
                              <span>{t('dashboard.nextMilestone') || 'Seuraava'}: {next} {t('dashboard.days') || 'pv'}</span>
                              <span>{streak.current_streak} / {next}</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-void/10 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-400 to-red-500 transition-all duration-700"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {!streak.streak_active && (
                    <Button
                      onClick={() => navigate('/journal')}
                      className="bg-gradient-to-r from-orange-400 to-red-500 text-white hover:opacity-90 rounded-full font-body font-medium"
                      data-testid="continue-streak-button"
                    >
                      <Flame className="w-4 h-4 mr-2" />
                      {t('dashboard.startStreak') || 'Start streak!'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* AI Art Styles Showcase — unlocked by streak milestones */}
            <ArtStylesShowcase
              user={user}
              recentDreamId={stats?.recent_dreams?.[0]?.id}
            />

            {streak && streak.artwork_credits > 0 && !user?.is_premium && (
              <div
                className="relative overflow-hidden rounded-3xl p-6 mb-8 bg-gradient-to-br from-amber-100 via-rose-50 to-purple-100 border-2 border-amber-300/60 shadow-lg animate-in fade-in slide-in-from-top-2 duration-500"
                data-testid="streak-reward-banner"
              >
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-amber-200 to-rose-200 rounded-full blur-2xl opacity-60" />
                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center shadow-md">
                      <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-light text-void mb-1">
                        {t('dashboard.streakReward.title')}
                      </h3>
                      <p className="text-sm text-void/70 font-body">
                        {t('dashboard.streakReward.subtitle', { count: streak.artwork_credits })}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const recent = stats?.recent_dreams?.[0];
                      if (recent?.id) {
                        navigate(`/dreams/${recent.id}`);
                      } else {
                        navigate('/dreams');
                      }
                    }}
                    className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white rounded-full font-body font-medium px-6"
                    data-testid="streak-reward-claim-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('dashboard.streakReward.claim')}
                  </Button>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1" data-testid="stat-total-dreams">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-ethereal/20 rounded-2xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-void" />
                  </div>
                  <span className="text-3xl font-heading font-light text-void">{stats?.total_dreams || 0}</span>
                </div>
                <h3 className="text-lg font-body text-void/80">{t('dashboard.totalDreams')}</h3>
              </div>

              <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1" data-testid="stat-week-dreams">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-lucid/20 rounded-2xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-void" />
                  </div>
                  <span className="text-3xl font-heading font-light text-void">{stats?.dreams_this_week || 0}</span>
                </div>
                <h3 className="text-lg font-body text-void/80">{t('dashboard.thisWeek')}</h3>
              </div>

              <div className="glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 hover:-translate-y-1" data-testid="stat-month-dreams">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-ethereal/20 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-void" />
                  </div>
                  <span className="text-3xl font-heading font-light text-void">{stats?.dreams_this_month || 0}</span>
                </div>
                <h3 className="text-lg font-body text-void/80">{t('dashboard.thisMonth')}</h3>
              </div>
            </div>

            {/* My Artworks shortcut — moved below dream map */}

            {/* Dream Summary - Premium Feature */}

            {/* Personal Dream Map */}
            {(stats?.total_dreams || 0) > 0 && (
              <div className="glass-effect rounded-3xl p-6 sm:p-8 mb-12" data-testid="personal-map-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <Map className="w-5 h-5 text-void" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-light text-void">
                      {t('personalMap.title') || 'Henkilökohtainen unikarttasi'}
                    </h3>
                    <p className="text-xs text-void/50 font-body">
                      {t('personalMap.subtitle') || 'Uniesi teemat ja tunteet visualisoituna'}
                    </p>
                  </div>
                </div>
                <PersonalDreamMap />
              </div>
            )}

            {/* My Artworks shortcut */}
            <button
              onClick={() => navigate('/my-artworks')}
              className="w-full glass-effect rounded-3xl p-5 mb-12 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 text-left group"
              data-testid="my-artworks-card"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ImageIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-light text-void">
                      {t('dashboard.myArtworks') || 'Unitaiteeni'}
                    </h3>
                    <p className="text-sm text-void/60 font-body">
                      {t('dashboard.myArtworksHint') || 'Selaa kaikkia AI:n luomia unitaide-kuviasi'}
                    </p>
                  </div>
                </div>
                <Sparkles className="w-5 h-5 text-purple-500 group-hover:rotate-12 transition-transform" />
              </div>
            </button>

            {/* Dream Summary - Premium Feature */}
            {user?.is_premium && (
              <div className="glass-effect rounded-3xl p-8 mb-8 ring-2 ring-lucid/40 bg-gradient-to-r from-lucid/10 to-ethereal/10" data-testid="dream-summary-section">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-lucid/30 rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-7 h-7 text-void" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-heading font-light text-void">
                        ✨ {t('dashboard.summary.title')}
                      </h2>
                      <p className="text-sm text-void/60 font-body">
                        {t('dashboard.summary.subtitle')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    <select
                      value={summaryPeriod}
                      onChange={(e) => setSummaryPeriod(e.target.value)}
                      className="bg-white/70 border-2 border-lucid/30 focus:border-lucid rounded-xl px-4 py-3 font-body text-void"
                    >
                      <option value="week">📅 {t('dashboard.summary.week')}</option>
                      <option value="month">📆 {t('dashboard.summary.month')}</option>
                    </select>
                    <select
                      value={summaryLanguage}
                      onChange={(e) => setSummaryLanguage(e.target.value)}
                      className="bg-white/70 border-2 border-lucid/30 focus:border-lucid rounded-xl px-4 py-3 font-body text-void"
                    >
                      <option value="finnish">🇫🇮 Suomeksi</option>
                      <option value="english">🇬🇧 English</option>
                      <option value="french">🇫🇷 Français</option>
                      <option value="german">🇩🇪 Deutsch</option>
                      <option value="spanish">🇪🇸 Español</option>
                    </select>
                    <Button
                      onClick={generateSummary}
                      disabled={summaryLoading}
                      className="bg-gradient-to-r from-lucid to-ethereal text-void hover:opacity-90 rounded-full font-body font-semibold px-6 py-3 text-base shadow-lg"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      {summaryLoading 
                        ? t('dashboard.summary.generating')
                        : t('dashboard.summary.generate')}
                    </Button>
                  </div>
                </div>

                {summary && (
                  <div className="bg-white/30 rounded-2xl p-6">
                    <p className="text-sm text-void/60 font-body mb-4">
                      {t('dashboard.summary.dreamsAnalyzed', { count: summary.dream_count })} ({summary.period === 'week' ? t('dashboard.summary.periodWeek') : t('dashboard.summary.periodMonth')})
                    </p>
                    <div className="prose prose-lg max-w-none">
                      {summary.summary.split('\n').map((paragraph, idx) => {
                        if (paragraph.trim().startsWith('##')) {
                          const text = paragraph.replace(/##/g, '').trim();
                          return (
                            <h3 key={idx} className="text-lg font-heading font-light text-void mt-4 mb-2">{text}</h3>
                          );
                        } else if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                          const text = paragraph.replace(/\*\*/g, '').trim();
                          return (
                            <h4 key={idx} className="text-md font-body font-medium text-void mt-3 mb-1">{text}</h4>
                          );
                        } else if (paragraph.trim()) {
                          return (
                            <p key={idx} className="text-void/80 font-body leading-relaxed mb-2">{paragraph}</p>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                )}

                {!summary && (
                  <p className="text-void/60 font-body text-center py-4">
                    {t('dashboard.summary.placeholder') || 'Generate an AI summary of your dreams from the selected period'}
                  </p>
                )}
              </div>
            )}

            {/* Recent Dreams and Tags */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Dreams */}
              <div className="glass-effect rounded-3xl p-8">
                <h2 className="text-2xl font-heading font-light text-void mb-6" data-testid="recent-dreams-title">{t('dashboard.recentDreams')}</h2>
                {stats?.recent_dreams && stats.recent_dreams.length > 0 ? (
                  <div className="space-y-4">
                    {stats.recent_dreams.map((dream) => (
                      <div
                        key={dream.id}
                        onClick={() => navigate(`/dreams/${dream.id}`)}
                        className="p-4 bg-white/30 rounded-2xl hover:bg-white/50 transition-all cursor-pointer"
                        data-testid={`recent-dream-${dream.id}`}
                      >
                        <h3 className="font-body font-medium text-void mb-1">{dream.title}</h3>
                        <p className="text-sm text-void/60 font-body line-clamp-2">{dream.content}</p>
                        <div className="flex gap-2 mt-2">
                          {dream.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-lucid/20 text-void rounded-full font-body">
                              {t(`tags.${tag}`) || tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-dreams-message">
                    <p className="text-void/60 font-body mb-4">{t('dashboard.noDreams')}</p>
                    <Button
                      onClick={() => navigate('/journal')}
                      className="bg-void text-white hover:bg-void/90 rounded-full font-body"
                      data-testid="start-recording-button"
                    >
                      {t('dashboard.startRecording')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Most Common Tags */}
              <div className="glass-effect rounded-3xl p-8">
                <h2 className="text-2xl font-heading font-light text-void mb-6" data-testid="common-tags-title">{t('dashboard.commonTags')}</h2>
                {stats?.most_common_tags && stats.most_common_tags.length > 0 ? (
                  <div className="space-y-3">
                    {stats.most_common_tags.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between" data-testid={`tag-${item.tag}`}>
                        <span className="font-body text-void">{t(`tags.${item.tag}`) || item.tag}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-white/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-lucid rounded-full"
                              style={{ width: `${(item.count / stats.total_dreams) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm text-void/60 font-body w-8 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-void/60 font-body text-center py-8" data-testid="no-tags-message">{t('dashboard.noTags')}</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-gray-200 md:hidden z-50 safe-bottom" data-testid="mobile-bottom-nav">
        <div className="flex justify-around items-center py-2 px-4">
          <button
            onClick={() => navigate('/dashboard')}
            className={`flex flex-col items-center p-2 ${location.pathname === '/dashboard' ? 'text-void' : 'text-gray-400'}`}
            data-testid="mobile-nav-home"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs mt-1">{t('nav.dashboard')}</span>
          </button>
          <button
            onClick={() => navigate('/dreams')}
            className={`flex flex-col items-center p-2 ${location.pathname === '/dreams' ? 'text-void' : 'text-gray-400'}`}
            data-testid="mobile-nav-dreams"
          >
            <BookOpen className="w-5 h-5" />
            <span className="text-xs mt-1">{t('nav.myDreams')}</span>
          </button>
          <button
            onClick={() => navigate('/journal')}
            className="flex flex-col items-center p-2 -mt-4"
            data-testid="mobile-nav-new-dream"
          >
            <div className="bg-void rounded-full p-3 shadow-lg">
              <PlusCircle className="w-6 h-6 text-white" />
            </div>
          </button>
          <button
            onClick={() => navigate('/circles')}
            className={`flex flex-col items-center p-2 ${location.pathname.includes('/circles') ? 'text-void' : 'text-gray-400'}`}
            data-testid="mobile-nav-circles"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs mt-1">{t('nav.circles') || 'Piirit'}</span>
          </button>
          <button
            onClick={() => navigate('/community-hub')}
            className={`flex flex-col items-center p-2 ${location.pathname.includes('/community') ? 'text-void' : 'text-gray-400'}`}
            data-testid="mobile-nav-community"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs mt-1">{t('nav.community')}</span>
          </button>
        </div>
      </nav>
      
      {/* Bottom padding for mobile nav */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}